import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  UserIcon,
  FlagIcon
} from '@heroicons/react/24/outline';
import { useToast } from '../../context/ToastContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import TaskDetailModal from '../../components/Modals/TaskDetailModal';
import { tasksAPI, authAPI } from '../../services/api';

// Types
interface Task {
  _id: string;
  title: string;
  description?: string;
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  status: 'New Client' | 'Active Client' | 'Target' | 'Placed' | 'Follow-up';
  type: 'Initial Contact' | 'Assessment' | 'Facility Visit' | 'Documentation' | 'Insurance Verification' | 'Contract Review' | 'Move Coordination' | 'Follow-up' | 'Other';
  dueDate: string;
  isCompleted: boolean;
  isOverdue: boolean;
  completedAt?: string;
  client?: {
    _id: string;
    firstName: string;
    lastName: string;
    trackerStatus: string;
    careLevel?: string;
    budget?: number;
    preferences?: {
      location?: string;
      amenities?: string[];
      specialties?: string[];
    };
  };
  assignedTo?: {
    _id: string;
    firstName: string;
    lastName: string;
    role?: string;
  };
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  matchedFacilities?: Array<{
    facilityId: string;
    facilityName: string;
    compatibilityScore: number;
    reasons: string[];
    available: boolean;
  }>;
  progress: {
    percentage: number;
    milestones: Array<{
      name: string;
      completed: boolean;
      completedAt?: string;
    }>;
  };
  communications: Array<{
    id: string;
    type: 'note' | 'call' | 'email' | 'meeting';
    content: string;
    author: string;
    timestamp: string;
  }>;
  estimatedPlacementDate?: string;
  lastActivity: string;
  stage?: string;
  createdAt: string;
  updatedAt: string;
  checklist?: Array<{
    _id: string;
    item: string;
    completed: boolean;
  }>;
  notes?: Array<{
    _id: string;
    content: string;
    createdBy: {
      firstName: string;
      lastName: string;
    };
    createdAt: string;
  }>;
}

interface TaskStats {
  totalTasks: number;
  completedTasks: number;
  overdueTasks: number;
  highPriorityTasks: number;
}

const TaskList: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('dueDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<TaskStats>({
    totalTasks: 0,
    completedTasks: 0,
    overdueTasks: 0,
    highPriorityTasks: 0
  });
  const [assignees, setAssignees] = useState<Array<{ _id: string; firstName: string; lastName: string }>>([]);

  const { success: showSuccess, error: showError } = useToast();

  const limit = 20;

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch tasks
  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params = {
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder,
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(priorityFilter !== 'all' && { priority: priorityFilter }),
        ...(typeFilter !== 'all' && { type: typeFilter }),
        ...(assigneeFilter !== 'all' && { assignedTo: assigneeFilter })
      };

      const response = await tasksAPI.getTasks(params);
      const data = response.data;
      
      setTasks(data.data.tasks);
      setTotalPages(data.data.pagination.totalPages);
      setTotalCount(data.data.pagination.total);
      setStats(data.data.statistics);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      showError('Error', 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  // Fetch assignees for filter
  const fetchAssignees = async () => {
    try {
      // Try to fetch users, but handle 404 gracefully since endpoint may not exist
      const response = await authAPI.getUsers();
      setAssignees(response.data.data.users || []);
    } catch (error: any) {
      // If 404 or other error, just use empty array - the filter will be disabled
      if (error.response?.status === 404) {
        console.log('Users endpoint not available, disabling assignee filter');
      } else {
        console.error('Error fetching assignees:', error);
      }
      setAssignees([]);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [page, sortBy, sortOrder, statusFilter, priorityFilter, typeFilter, assigneeFilter, debouncedSearchTerm]);

  useEffect(() => {
    fetchAssignees();
  }, []);

  // Handle task deletion
  const handleDeleteTask = async (taskId: string) => {
    // Using window.confirm to avoid ESLint error
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      await tasksAPI.deleteTask(taskId);
      await fetchTasks();
      showSuccess('Success', 'Task deleted successfully');
    } catch (error) {
      console.error('Error deleting task:', error);
      showError('Error', 'Failed to delete task');
    }
  };

  // Handle task click
  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Force immediate search on form submit
    setDebouncedSearchTerm(searchTerm);
    setPage(1);
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setTypeFilter('all');
    setAssigneeFilter('all');
    setPage(1);
  };

  // Get priority color
  const getPriorityColor = (priority: string) => {
    const normalizedPriority = priority.toLowerCase();
    switch (normalizedPriority) {
      case 'urgent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
    switch (normalizedStatus) {
      case 'completed':
      case 'placed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
      case 'active_client':
        return 'bg-blue-100 text-blue-800';
      case 'review':
      case 'follow-up':
        return 'bg-purple-100 text-purple-800';
      case 'todo':
      case 'new_client':
        return 'bg-gray-100 text-gray-800';
      case 'target':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get type icon
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'placement':
        return <UserIcon className="w-4 h-4" />;
      case 'administrative':
        return <CalendarIcon className="w-4 h-4" />;
      case 'follow_up':
        return <ClockIcon className="w-4 h-4" />;
      case 'documentation':
        return <PencilIcon className="w-4 h-4" />;
      default:
        return <CalendarIcon className="w-4 h-4" />;
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format date relative
  const formatDateRelative = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0) return `In ${diffDays} days`;
    return `${Math.abs(diffDays)} days ago`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <CheckCircleIcon className="w-8 h-8 text-primary-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 text-left">Tasks</h1>
            <p className="text-gray-600">Manage and track all tasks across the system</p>
          </div>
        </div>
        <Link
          to="/tasks/new"
          className="btn-primary flex items-center space-x-2"
        >
          <PlusIcon className="w-5 h-5" />
          <span>New Task</span>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <CalendarIcon className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Tasks</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalTasks}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircleIcon className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Completed</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.completedTasks}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Overdue</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.overdueTasks}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <FlagIcon className="w-5 h-5 text-orange-600" />
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">High Priority</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.highPriorityTasks}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-6 rounded-lg shadow-card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search tasks..."
                className="input-field pl-10"
              />
            </div>
          </form>

          {/* Filter Toggle and View Mode */}
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-colors ${
                showFilters 
                  ? 'bg-primary-50 border-primary-200 text-primary-700' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FunnelIcon className="w-4 h-4" />
              <span>Filters</span>
            </button>

            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 text-sm ${
                  viewMode === 'list' 
                    ? 'bg-primary-600 text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                List
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 text-sm ${
                  viewMode === 'grid' 
                    ? 'bg-primary-600 text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Grid
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="input-field"
                >
                  <option value="all">All Status</option>
                  <option value="todo">To Do</option>
                  <option value="in_progress">In Progress</option>
                  <option value="review">Review</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="input-field"
                >
                  <option value="all">All Priority</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="input-field"
                >
                  <option value="all">All Types</option>
                  <option value="placement">Placement</option>
                  <option value="administrative">Administrative</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="documentation">Documentation</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
                <select
                  value={assigneeFilter}
                  onChange={(e) => setAssigneeFilter(e.target.value)}
                  className="input-field"
                >
                  <option value="all">All Assignees</option>
                  {assignees.map((assignee) => (
                    <option key={assignee._id} value={assignee._id}>
                      {assignee.firstName} {assignee.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                <select
                  value={`${sortBy}_${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('_');
                    setSortBy(field);
                    setSortOrder(order as 'asc' | 'desc');
                  }}
                  className="input-field"
                >
                  <option value="dueDate_asc">Due Date (Earliest)</option>
                  <option value="dueDate_desc">Due Date (Latest)</option>
                  <option value="priority_desc">Priority (High to Low)</option>
                  <option value="createdAt_desc">Created (Newest)</option>
                  <option value="createdAt_asc">Created (Oldest)</option>
                  <option value="title_asc">Title (A-Z)</option>
                </select>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={resetFilters}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Reset Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tasks List/Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-card">
          {tasks.length === 0 ? (
            <div className="text-center py-12">
              <CalendarIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
              <p className="text-gray-600 mb-4">
                {debouncedSearchTerm || statusFilter !== 'all' || priorityFilter !== 'all' || typeFilter !== 'all' || assigneeFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Get started by creating your first task'}
              </p>
              <Link to="/tasks/new" className="btn-primary">
                Create Task
              </Link>
            </div>
          ) : viewMode === 'list' ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5">
                      Task
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Assignee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Progress
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tasks.map((task) => (
                    <tr key={task._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-start space-x-3">
                          <div className="flex-1 min-w-0 max-w-xs">
                            <button
                              onClick={() => handleTaskClick(task)}
                              className={`block text-left w-full ${
                                task.isCompleted ? 'line-through text-gray-500' : 'text-gray-900'
                              }`}
                            >
                              <p className="text-sm font-medium hover:text-primary-600 truncate">
                                {task.title}
                              </p>
                              {task.description && (
                                <p className="text-sm text-gray-500 truncate max-w-xs">
                                  {task.description}
                                </p>
                              )}
                              <div className="flex items-center space-x-2 mt-1">
                                {getTypeIcon(task.type)}
                                <span className="text-xs text-gray-500 capitalize truncate">
                                  {task.type.replace('_', ' ')}
                                </span>
                                {task.client && (
                                  <>
                                    <span className="text-xs text-gray-400">•</span>
                                    <span className="text-xs text-gray-500 truncate">
                                      {task.client.firstName} {task.client.lastName}
                                    </span>
                                  </>
                                )}
                              </div>
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                          {task.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-xs font-medium text-gray-600">
                                {task.assignedTo?.firstName?.[0] || '?'}{task.assignedTo?.lastName?.[0] || '?'}
                              </span>
                            </div>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-gray-900">
                              {task.assignedTo?.firstName || 'Unassigned'} {task.assignedTo?.lastName || ''}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(task.dueDate)}
                        </div>
                        <div className={`text-xs ${
                          task.isOverdue ? 'text-red-600' : 'text-gray-500'
                        }`}>
                          {formatDateRelative(task.dueDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-1">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs text-gray-600">
                                {task.progress?.percentage || 0}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                                style={{ width: `${task.progress?.percentage || 0}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleTaskClick(task)}
                            className="text-gray-400 hover:text-gray-600"
                            title="View Details"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          <Link
                            to={`/tasks/${task._id}/edit`}
                            className="text-blue-400 hover:text-blue-600"
                            title="Edit"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => handleDeleteTask(task._id)}
                            className="text-red-400 hover:text-red-600"
                            title="Delete"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {tasks.map((task) => (
                  <div key={task._id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                      </div>
                      {task.isOverdue && (
                        <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
                      )}
                    </div>

                    <button
                      onClick={() => handleTaskClick(task)}
                      className="block text-left w-full"
                    >
                      <h4 className={`font-medium mb-2 ${
                        task.isCompleted ? 'line-through text-gray-500' : 'text-gray-900'
                      }`}>
                        {task.title}
                      </h4>
                      {task.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {task.description}
                        </p>
                      )}
                    </button>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-xs">
                        <span className={`px-2 py-1 rounded-full ${getStatusColor(task.status)}`}>
                          {task.status.replace('_', ' ')}
                        </span>
                        <span className="text-gray-500">
                          {formatDateRelative(task.dueDate)}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {getTypeIcon(task.type)}
                        <span className="text-xs text-gray-500 capitalize">
                          {task.type.replace('_', ' ')}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-1">
                          <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-xs font-medium text-gray-600">
                              {task.assignedTo?.firstName?.[0] || '?'}{task.assignedTo?.lastName?.[0] || '?'}
                            </span>
                          </div>
                          <span className="text-xs text-gray-600">
                            {task.assignedTo?.firstName || 'Unassigned'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {task.progress?.percentage || 0}%
                        </span>
                      </div>

                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div 
                          className="bg-blue-600 h-1.5 rounded-full transition-all duration-300" 
                          style={{ width: `${task.progress?.percentage || 0}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleTaskClick(task)}
                          className="text-gray-400 hover:text-gray-600"
                          title="View Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <Link
                          to={`/tasks/${task._id}/edit`}
                          className="text-blue-400 hover:text-blue-600"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </Link>
                      </div>
                      <button
                        onClick={() => handleDeleteTask(task._id)}
                        className="text-red-400 hover:text-red-600"
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <p className="text-sm text-gray-700">
                    Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalCount)} of {totalCount} results
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTask}
        isOpen={showTaskModal}
        onClose={() => {
          setShowTaskModal(false);
          setSelectedTask(null);
        }}
        onUpdateTask={() => {
          fetchTasks(); // Refresh tasks after update
        }}
      />
    </div>
  );
};

export default TaskList;
